import Image from 'next/image';
import { useAnalyticsTotal } from '@/hooks/useAnalytics';
import { cx, parseEncodedTextToNodes } from '@/utils';
import { useLayoutContext } from '@common/Layout/LayoutProvider/LayoutContext';

import CustomLink from '../../CustomLink';
import { RouterType } from '../../Providers/GraphqlProvider/generated';
import FooterLinks from './FooterLinks';
import RichText from './RichText';
import SocialNetworks from './SocialNetworks';
import styles from './styles.module.css';

const FooterInfo = () => {
  const {
    layout: { pageFooterBar },
  } = useLayoutContext();
  const mInfo = pageFooterBar?.campusData || '';

  const redirectToCs3 = () => {
    window.open('https://www.cs3.com.co/', '_blank');
  };

  return (
    <section className={styles.info}>
      <div
        className={cx(
          styles.infoContent,
          'inner 2xl:mx-auto 2xl:w-full 2xl:max-w-[1125px] 3xl:max-w-[1305px]'
        )}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[8fr_4fr]">
          <div className="headline-h5 col-start-1 row-start-1 mb-3 text-start text-primary lg:col-start-1 lg:row-start-1 lg:mb-0">
            {pageFooterBar?.title}
          </div>
          <div className="col-start-1 row-start-2 lg:col-start-2 lg:row-start-1">
            <div className="flex min-w-0 items-center gap-2 md:gap-5 lg:justify-end">
              {pageFooterBar?.logoImage && (
                <>
                  <CustomLink
                    pageLink={{
                      id: '1',
                      createdAt: null,
                      updatedAt: null,
                      routeType: RouterType.ExternalRoute,
                      target: '_blank',
                      url: pageFooterBar?.logoLink,
                    }}
                  >
                    <Image
                      src={pageFooterBar?.logoImage.downloadUrl ?? ''}
                      alt={pageFooterBar?.title || pageFooterBar?.logoImage.fileName || 'logo'}
                      width={60}
                      height={40}
                      className="h-[40px] w-auto object-contain lg:h-[40px]"
                    />
                  </CustomLink>
                  <div className="w-[1px] self-stretch bg-[#3366CC] lg:block" />
                </>
              )}

              {pageFooterBar?.administrationLogo && (
                <CustomLink
                  className="relative h-[40px] w-auto lg:h-[60px]"
                  pageLink={{
                    id: '2',
                    createdAt: null,
                    updatedAt: null,
                    routeType: RouterType.ExternalRoute,
                    target: '_blank',
                    url: pageFooterBar?.administrationLogoLink,
                  }}
                >
                  <Image
                    src={pageFooterBar.administrationLogo?.downloadUrl ?? ''}
                    alt={
                      pageFooterBar?.title || pageFooterBar.administrationLogo?.fileName || 'logo'
                    }
                    width={80}
                    height={60}
                    className="h-full w-auto object-contain"
                  />
                </CustomLink>
              )}
              {pageFooterBar?.adminOtherLogo && (
                <CustomLink
                  className="relative h-[40px] w-auto lg:h-[40px]"
                  pageLink={{
                    id: '3',
                    createdAt: null,
                    updatedAt: null,
                    routeType: RouterType.ExternalRoute,
                    target: '_blank',
                    url: pageFooterBar?.administrationLogoLink,
                  }}
                >
                  <Image
                    src={pageFooterBar?.adminOtherLogo.downloadUrl ?? ''}
                    alt={pageFooterBar?.title || pageFooterBar?.adminOtherLogo.fileName || 'logo'}
                    width={60}
                    height={40}
                    className="h-full w-auto object-contain"
                  />
                </CustomLink>
              )}
            </div>
          </div>

          {/* Iframe Hora Legal Colombia */}
          <div className="col-start-1 row-start-3 -ml-2 flex flex-col items-center gap-4 lg:col-start-2 lg:row-start-2 lg:ml-0 lg:items-end">
            <iframe
              src="https://horalegal.inm.gov.co/inm/"
              title="Hora Legal Colombia"
              width="340"
              height="70"
              scrolling="no"
              style={{ background: 'transparent', border: 'none', margin: 0, padding: 0 }}
            />
            <VisitsCounter />
          </div>

          <div className="col-start-1 row-start-4 lg:row-span-2 lg:row-start-2">
            <div>
              <div className={'headline-h6 mb-3 text-primary'}>{pageFooterBar?.subtitle}</div>
              <div className="font-workSans text-[16px] leading-[24px] text-black">
                <RichText nodes={parseEncodedTextToNodes(mInfo)} />
              </div>
            </div>
          </div>

          {/* Social Networks row (full width) */}
          <div className="col-span-2">
            <SocialNetworks color={'text-primary'} />
          </div>

          {/* Footer Links row (full width) */}
          <div className="col-span-2">
            <FooterLinks />
          </div>
        </div>
      </div>

      <div
        onClick={redirectToCs3}
        className={'inner my-5 flex cursor-pointer flex-row justify-center gap-2 md:justify-end'}
      >
        <div className={'body-text1 text-white'}>{'Desarrollado por'}</div>
        <Image src="/cs3_logo_white.png" alt="CS3 Logo" width={191} height={30} />
      </div>
    </section>
  );
};

const VisitsCounter = () => {
  const { total, isLoading } = useAnalyticsTotal();

  if (isLoading || total === undefined || total === null) return null;

  return (
    <div className="flex w-[260px] items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-blue-600"
        >
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-500">Número de visitas</span>
        <span className="text-3xl font-semibold text-gray-800">
          {new Intl.NumberFormat('es-CO').format(total)}
        </span>
      </div>
    </div>
  );
};

export default FooterInfo;
